import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Fonte única do consumo usado para alocar: prefere a média real das faturas e
// cai na estimativa cadastral quando ela não é confiável. Antes cada ponto lia
// `u.consumo_medio_kwh || 0` direto, e trocar a fonte em vários lugares é como
// se cria divergência entre a tela e a validação do servidor.
import { consumoParaAlocacao } from '../lib/consumoDaUc';

export type RateioRowState = {
  ucId: number;
  percent: number;
  locked: boolean;
  energiaAlvoKwh: number;
};

export type RateioModalidade = 'percentual_fixo' | 'ordem_prioridade';
export type Gargalo = 'reducao_proporcional' | 'priorizacao_seletiva';

export type UcParaRateio = {
  id: number;
  numero_uc: string;
  outro_numero_uc?: string | null;
  nome_cliente?: string | null;
  cpf_cnpj_titular?: string | null;
  cpf_cnpj_cliente?: string | null;
  /** Estimativa comercial congelada da análise cadastral — fallback, não a fonte primária. */
  consumo_medio_kwh?: number | null;
  /** Média real das faturas, gravada pela rotina diária. É a fonte primária da alocação. */
  consumo_observado_kwh?: number | null;
  consumo_observado_meses?: number | null;
  /** Acima de 0,6 a UC é marcada como instável: o número vale, mas não pausa a UC. */
  consumo_observado_dispersao?: number | null;
  consumo_observado_em?: string | null;
  situacao?: string | null;
  rateio_unidade_geradora_id?: string | null;
  rateio_travado_em?: string | null;
  rateio_motivo_travamento?: 'excesso_saldo' | 'manual' | null;
  rateio_destravado_por?: string | null;
  rateio_destravado_em?: string | null;
  modalidade_gd?: 'GD_I' | 'GD_II' | null;
  rateio_vinculado_em?: string | null;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

// Absorve sobra/falta de arredondamento na linha de maior percentual, garantindo soma == 100.00 exatos.
// Só usado em Percentual Fixo — em Ordem de Prioridade quem absorve a diferença é a Unidade Âncora.
function aplicarAjusteArredondamento(rows: RateioRowState[]): RateioRowState[] {
  if (rows.length === 0) return rows;
  const soma = round2(rows.reduce((s, r) => s + r.percent, 0));
  const delta = round2(100 - soma);
  if (delta === 0) return rows;
  const idx = rows.reduce((best, r, i) => (r.percent > rows[best].percent ? i : best), 0);
  const ajustadas = [...rows];
  ajustadas[idx] = { ...ajustadas[idx], percent: round2(ajustadas[idx].percent + delta) };
  return ajustadas;
}

function comEnergiaAlvo(rows: RateioRowState[], excedenteAlocavelKwh: number): RateioRowState[] {
  return rows.map(r => ({ ...r, energiaAlvoKwh: round2((r.percent / 100) * excedenteAlocavelKwh) }));
}

// Percentual de cada UC = quanto o consumo médio dela representa do total da UG
// (excedenteAlocavelKwh), não uma partilha entre as UCs vinculadas entre si —
// mesma base que aplicarSugestaoDelta já usa. Por isso a soma da sugestão NÃO
// fecha 100% na maioria dos casos (só fecha se a soma dos consumos médios
// coincidir com o excedente) — é o ponto de partida esperado pela spec
// (Seção 4.3), o Analista trava/ajusta manualmente até fechar 100%. Por isso
// também NÃO aplica aplicarAjusteArredondamento aqui: essa função existe pra
// absorver ruído de arredondamento (When a soma já fecha ~100%), não pra
// mascarar uma diferença real forçando-a numa única linha.
export function gerarSugestaoInicial(ucs: UcParaRateio[], excedenteAlocavelKwh: number): RateioRowState[] {
  const n = ucs.length;
  if (n === 0) return [];
  const rows: RateioRowState[] = ucs.map(u => ({
    ucId: u.id,
    percent:
      excedenteAlocavelKwh > 0 ? round2((consumoParaAlocacao(u) / excedenteAlocavelKwh) * 100) : round2(100 / n),
    locked: false,
    energiaAlvoKwh: 0,
  }));
  return comEnergiaAlvo(rows, excedenteAlocavelKwh);
}

// Edição manual de UC destravada: reparte o restante entre as demais destravadas
// proporcionalmente ao peso atual delas.
export function onEditarPercentual(
  rows: RateioRowState[],
  ucId: number,
  novoValorDigitado: number,
  excedenteAlocavelKwh: number
): RateioRowState[] {
  const alvo = rows.find(r => r.ucId === ucId);
  if (!alvo || alvo.locked) return rows;

  const somaTravada = round2(rows.filter(r => r.locked).reduce((s, r) => s + r.percent, 0));
  const tetoDisponivel = round2(Math.max(0, 100 - somaTravada));
  const novoValor = Math.min(Math.max(round2(novoValorDigitado), 0), tetoDisponivel);

  const outrasDestravadas = rows.filter(r => !r.locked && r.ucId !== ucId);
  const restante = round2(tetoDisponivel - novoValor);
  const pesoBase = outrasDestravadas.reduce((s, r) => s + r.percent, 0);

  const ajustadas = outrasDestravadas.map(r => ({
    ...r,
    percent:
      pesoBase > 0
        ? round2((r.percent / pesoBase) * restante)
        : round2(restante / (outrasDestravadas.length || 1)),
  }));

  const resultado = rows.map(r => {
    if (r.ucId === ucId) return { ...r, percent: novoValor };
    if (r.locked) return r;
    return ajustadas.find(a => a.ucId === r.ucId)!;
  });

  return comEnergiaAlvo(aplicarAjusteArredondamento(resultado), excedenteAlocavelKwh);
}

// Travar/destravar uma linha: redistribui entre as destravadas restantes.
export function onToggleLock(rows: RateioRowState[], ucId: number, excedenteAlocavelKwh: number): RateioRowState[] {
  const novasLinhas = rows.map(r => (r.ucId === ucId ? { ...r, locked: !r.locked } : r));
  const somaTravada = round2(novasLinhas.filter(r => r.locked).reduce((s, r) => s + r.percent, 0));
  const destravadas = novasLinhas.filter(r => !r.locked);

  if (destravadas.length === 0) return novasLinhas;

  const restante = round2(Math.max(0, 100 - somaTravada));
  const pesoBase = destravadas.reduce((s, r) => s + r.percent, 0);
  const ajustadas = destravadas.map(r => ({
    ...r,
    percent: pesoBase > 0 ? round2((r.percent / pesoBase) * restante) : round2(restante / destravadas.length),
  }));

  const resultado = novasLinhas.map(r => (r.locked ? r : ajustadas.find(a => a.ucId === r.ucId)!));
  return comEnergiaAlvo(aplicarAjusteArredondamento(resultado), excedenteAlocavelKwh);
}

export type PriorityRow = { ucId: number; consumoMedioKwh: number };

/**
 * Aloca energia por ordem de prioridade (cascata) em kWh.
 * - Sobra (soma dos consumos médios <= excedente): cada UC recebe integralmente seu consumo
 *   médio; o restante vira `ancoraKwh` (absorvido automaticamente pela Unidade Âncora).
 * - Déficit + 'reducao_proporcional': todo mundo é reduzido pela mesma razão, ninguém zera.
 * - Déficit + 'priorizacao_seletiva': cascata original — quem fica no fim da fila pode zerar.
 */
export function alocarPorPrioridade(
  ordemUcs: PriorityRow[],
  excedenteAlocavelKwh: number,
  gargalo: Gargalo
): { linhas: RateioRowState[]; ancoraKwh: number } {
  const somaConsumo = round2(ordemUcs.reduce((s, u) => s + Math.max(0, u.consumoMedioKwh || 0), 0));
  let energiaPorUc: Map<number, number>;

  if (somaConsumo <= excedenteAlocavelKwh) {
    energiaPorUc = new Map(ordemUcs.map(u => [u.ucId, round2(Math.max(0, u.consumoMedioKwh || 0))]));
  } else if (gargalo === 'reducao_proporcional') {
    const fator = somaConsumo > 0 ? excedenteAlocavelKwh / somaConsumo : 0;
    energiaPorUc = new Map(ordemUcs.map(u => [u.ucId, round2(Math.max(0, u.consumoMedioKwh || 0) * fator)]));
  } else {
    let restante = excedenteAlocavelKwh;
    energiaPorUc = new Map();
    for (const u of ordemUcs) {
      const teto = Math.max(0, u.consumoMedioKwh || 0);
      const alocado = round2(Math.max(0, Math.min(teto, restante)));
      energiaPorUc.set(u.ucId, alocado);
      restante = round2(restante - alocado);
    }
  }

  const somaAlocada = round2(Array.from(energiaPorUc.values()).reduce((s, v) => s + v, 0));
  const ancoraKwh = round2(Math.max(0, excedenteAlocavelKwh - somaAlocada));

  const linhas: RateioRowState[] = ordemUcs.map(u => {
    const energiaAlvoKwh = energiaPorUc.get(u.ucId) || 0;
    return {
      ucId: u.ucId,
      energiaAlvoKwh,
      percent: excedenteAlocavelKwh > 0 ? round2((energiaAlvoKwh / excedenteAlocavelKwh) * 100) : 0,
      locked: false,
    };
  });

  return { linhas, ancoraKwh };
}

export function reordenar<T>(lista: T[], sourceIndex: number, destIndex: number): T[] {
  const nova = [...lista];
  const [movido] = nova.splice(sourceIndex, 1);
  nova.splice(destIndex, 0, movido);
  return nova;
}

export function somaPercentual(rows: RateioRowState[]): number {
  return round2(rows.reduce((s, r) => s + r.percent, 0));
}

export function podeSalvar(rows: RateioRowState[]): boolean {
  return rows.length > 0 && Math.abs(somaPercentual(rows) - 100) < 0.005;
}

/**
 * Hook que orquestra o estado do simulador (linhas por UC, modalidade, ordem de
 * prioridade, gargalo) e expõe as ações da grade. Lógica pura de cálculo vive nas
 * funções acima (testáveis isoladamente, sem I/O); este hook só guarda o estado em React.
 */
export function useRateioBalanceamento(excedenteAlocavelKwh: number) {
  const [modalidade, setModalidade] = useState<RateioModalidade>('percentual_fixo');
  const [gargalo, setGargaloState] = useState<Gargalo>('priorizacao_seletiva');
  const [rows, setRows] = useState<RateioRowState[]>([]);
  const [ordemPrioridade, setOrdemPrioridade] = useState<PriorityRow[]>([]);
  const [ancoraKwh, setAncoraKwh] = useState(0);

  // Quando o Excedente Alocável muda (input manual do Analista), recalcula a
  // Energia-Alvo das linhas já existentes SEM tocar nos percentuais definidos —
  // evita perder edições manuais/travas do usuário só porque ele ajustou o excedente.
  const primeiraRenderizacao = useRef(true);
  useEffect(() => {
    if (primeiraRenderizacao.current) {
      primeiraRenderizacao.current = false;
      return;
    }
    if (modalidade === 'percentual_fixo') {
      setRows(prev => comEnergiaAlvo(prev, excedenteAlocavelKwh));
    } else {
      setOrdemPrioridade(prev => {
        const { linhas, ancoraKwh: ancora } = alocarPorPrioridade(prev, excedenteAlocavelKwh, gargalo);
        setRows(linhas);
        setAncoraKwh(ancora);
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excedenteAlocavelKwh]);

  const inicializar = useCallback(
    (ucs: UcParaRateio[]) => {
      setRows(gerarSugestaoInicial(ucs, excedenteAlocavelKwh));
      setOrdemPrioridade(ucs.map(u => ({ ucId: u.id, consumoMedioKwh: consumoParaAlocacao(u) })));
      setAncoraKwh(0);
    },
    [excedenteAlocavelKwh]
  );

  const editarPercentual = useCallback(
    (ucId: number, novoValor: number) => {
      setRows(prev => onEditarPercentual(prev, ucId, novoValor, excedenteAlocavelKwh));
    },
    [excedenteAlocavelKwh]
  );

  const toggleLock = useCallback(
    (ucId: number) => {
      setRows(prev => onToggleLock(prev, ucId, excedenteAlocavelKwh));
    },
    [excedenteAlocavelKwh]
  );

  const arrastar = useCallback(
    (sourceIndex: number, destIndex: number) => {
      setOrdemPrioridade(prev => {
        const nova = reordenar(prev, sourceIndex, destIndex);
        const { linhas, ancoraKwh: ancora } = alocarPorPrioridade(nova, excedenteAlocavelKwh, gargalo);
        setRows(linhas);
        setAncoraKwh(ancora);
        return nova;
      });
    },
    [excedenteAlocavelKwh, gargalo]
  );

  const setGargalo = useCallback(
    (novoGargalo: Gargalo) => {
      setGargaloState(novoGargalo);
      setOrdemPrioridade(prev => {
        const { linhas, ancoraKwh: ancora } = alocarPorPrioridade(prev, excedenteAlocavelKwh, novoGargalo);
        setRows(linhas);
        setAncoraKwh(ancora);
        return prev;
      });
    },
    [excedenteAlocavelKwh]
  );

  const trocarModalidade = useCallback(
    (nova: RateioModalidade, ucs: UcParaRateio[]) => {
      setModalidade(nova);
      if (nova === 'percentual_fixo') {
        setRows(gerarSugestaoInicial(ucs, excedenteAlocavelKwh));
        setAncoraKwh(0);
      } else {
        const ordem = ucs.map(u => ({ ucId: u.id, consumoMedioKwh: consumoParaAlocacao(u) }));
        setOrdemPrioridade(ordem);
        const { linhas, ancoraKwh: ancora } = alocarPorPrioridade(ordem, excedenteAlocavelKwh, gargalo);
        setRows(linhas);
        setAncoraKwh(ancora);
      }
    },
    [excedenteAlocavelKwh, gargalo]
  );

  /**
   * Aplica a Sugestão do popover de Delta (Revisão Inteligente) pra uma UC —
   * SÓ na simulação/rascunho atual, nunca no cadastro da UC (consumo_medio_kwh
   * nunca é escrito, nem localmente nem no Supabase). Reaproveita as mesmas
   * primitivas usadas quando o usuário edita manualmente.
   */
  const aplicarSugestaoDelta = useCallback(
    (ucId: number, consumoRealKwh: number) => {
      if (modalidade === 'percentual_fixo') {
        const novoPercentAlvo = excedenteAlocavelKwh > 0 ? round2((consumoRealKwh / excedenteAlocavelKwh) * 100) : 0;
        setRows(prev => onEditarPercentual(prev, ucId, novoPercentAlvo, excedenteAlocavelKwh));
      } else {
        setOrdemPrioridade(prev => {
          const nova = prev.map(p => (p.ucId === ucId ? { ...p, consumoMedioKwh: consumoRealKwh } : p));
          const { linhas, ancoraKwh: ancora } = alocarPorPrioridade(nova, excedenteAlocavelKwh, gargalo);
          setRows(linhas);
          setAncoraKwh(ancora);
          return nova;
        });
      }
    },
    [modalidade, excedenteAlocavelKwh, gargalo]
  );

  // Recarrega um rascunho já salvo (percentuais, locks, ordem, gargalo e Âncora tal
  // como gravados), sem passar pela sugestão automática — usado ao reabrir UG+mês já simulados antes.
  const hidratar = useCallback(
    (
      novaModalidade: RateioModalidade,
      novasRows: RateioRowState[],
      novaOrdem: PriorityRow[],
      novoAncoraKwh: number,
      novoGargalo: Gargalo | null
    ) => {
      setModalidade(novaModalidade);
      setRows(novasRows);
      setOrdemPrioridade(novaOrdem);
      setAncoraKwh(novoAncoraKwh);
      setGargaloState(novoGargalo || 'priorizacao_seletiva');
    },
    []
  );

  const soma = useMemo(() => somaPercentual(rows), [rows]);
  const habilitaSalvar = useMemo(
    () => (modalidade === 'percentual_fixo' ? podeSalvar(rows) : rows.length > 0),
    [modalidade, rows]
  );

  return {
    modalidade,
    gargalo,
    rows,
    ordemPrioridade,
    ancoraKwh,
    soma,
    habilitaSalvar,
    inicializar,
    editarPercentual,
    toggleLock,
    arrastar,
    setGargalo,
    trocarModalidade,
    hidratar,
    aplicarSugestaoDelta,
  };
}
